import React, { useEffect, useState } from 'react';
import { Helix2000Character } from '../../../shared/CharacterTypes';
import { CharacterPortrait } from '../components/CharacterPortrait';
import { playerInfoSnapshot } from './playerInfoSnapshot';
import './PlayerInfoPanel.css';
import { VitalsPanel } from './VitalsPanel';
import {
  COLORS, SPACE, FONT_SIZE, RADII, ACCENT, INK, CHROME,
  classColor, classIcon, formatCallingName, PLAYER_THEME_VARS,
} from '../theme';
import { EventBus, EVENTS } from '../../../shared/EventBus';
import { useHudMood } from '../contexts/HudMoodContext';
import { comboPointState } from '../../systems/combat/ComboPointSystem';
import { REGEN } from '../../../shared/config/regen.config';
import { isRegeneratingHealth, useServerInCombat } from './useServerInCombat';
import { usePlayerVital } from '../hooks/usePlayerVital';
import { windowBridge } from '../framework/windowBridge';
import { ComboPip } from '../Systems/StarCounter';
import { HudVine } from './tome/hudOrnaments';
import type { HudConcreteStyle } from './useHudTomeVibe';

export interface PlayerInfoPanelProps {
  player: Helix2000Character;
  renderKey?: string;
  hudStyle?: HudConcreteStyle;
}

const getClassColor = (className: string): string => classColor(className);
const getClassIcon = (className: string): string => classIcon(className);
const formatClassName = (className: string): string => formatCallingName(className);

/** Live combo-point pool — initial read from comboPointState singleton */
const useComboPoints = (): { points: number; max: number } => {
  const [combo, setCombo] = useState(() => {
    const snap = comboPointState.read();
    return { points: snap.points, max: snap.max };
  });
  useEffect(() => {
    return EventBus.getInstance().on(
      EVENTS.COMBO_POINTS_CHANGED,
      (payload: { points: number; max: number }) =>
        setCombo({ points: payload.points, max: payload.max }),
    );
  }, []);
  return combo;
};

/** Star-count row for combo-point classes (rogue) */
const ComboPointRow: React.FC<{ points: number; max: number }> = ({ points, max }) => (
  <div
    className="combo-point-row"
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      marginBottom: SPACE.sm,
      fontSize: FONT_SIZE.sm,
      lineHeight: 1,
    }}
  >
    {Array.from({ length: max }, (_, i) => (
      <ComboPip key={i} active={i < points} isMaxed={points >= max} size={12} />
    ))}
    <span
      style={{
        marginLeft: SPACE.xs,
        fontSize: FONT_SIZE.xs,
        color: points >= max ? '#f59e0b' : COLORS.textSecondary,
        fontWeight: points >= max ? 700 : 500,
      }}
    >
      {points}/{max}
    </span>
  </div>
);

const PlayerInfoPanelInner: React.FC<PlayerInfoPanelProps> = ({ player, hudStyle }) => {
  const classCol = getClassColor(player.class);
  const combo = useComboPoints();
  const { moodTint } = useHudMood();
  const lowHp = usePlayerVital(player, (v) => v.maxHealth > 0 && v.health / v.maxHealth < 0.3);
  const inCombat = useServerInCombat();
  const regenerating = usePlayerVital(player, (v) =>
    isRegeneratingHealth(
      { ...v, isDead: (player as { isDead?: boolean }).isDead },
      inCombat,
      REGEN.health.outOfCombatPctPerSecond,
    ),
  );

  return (
    <div
      className={`player-info-panel${hudStyle ? ' hudt-frame' : ''}`}
      data-hud-style={hudStyle}
      data-low-health={lowHp ? 'true' : 'false'}
      data-in-combat={inCombat ? 'true' : 'false'}
      data-low={lowHp || undefined}
      data-combat={inCombat || undefined}
      style={{
        ...PLAYER_THEME_VARS,
        position: 'relative',
        ...(hudStyle
          ? {}
          : {
            background: `linear-gradient(${moodTint}, ${moodTint}), linear-gradient(180deg, ${INK.hi}, ${INK.base} 55%, ${INK.lo})`,
            border: `1px solid ${lowHp ? 'rgba(227, 93, 93, 0.6)' : ACCENT.brass}`,
            borderRadius: RADII.lg,
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
            animation: lowHp ? 'helix-lowhp-pulse 2s ease-in-out infinite' : undefined,
          }),
      }}
    >
      {hudStyle ? (
        <>
          <span className="hudt-ground" aria-hidden="true" />
          <HudVine at="tl" />
          <HudVine at="br" />
        </>
      ) : (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            left: 10,
            right: 10,
            top: 0,
            height: 2,
            backgroundImage: `url("${CHROME.panelEdgeLine()}")`,
            backgroundSize: '100% 100%',
            backgroundRepeat: 'no-repeat',
            opacity: 0.5,
            pointerEvents: 'none',
          }}
        />
      )}

      <header className={`player-frame-header${hudStyle ? ' hudt-head' : ''}`}>
        <div className={`player-frame-portrait${hudStyle ? ' hudt-portrait' : ''}`}>
          <CharacterPortrait
            appearance={player.appearance}
            characterClass={player.class}
            equipmentVisuals={player.equipmentVisuals!}
            size={44}
            label={`${player.name}, current look`}
          />
        </div>
        <div className={`player-frame-identity${hudStyle ? ' hudt-id' : ''}`}>
          <div className="player-frame-name">
            <strong className={hudStyle ? 'hudt-name' : undefined} title={player.name}>
              {player.name}
            </strong>
            {hudStyle ? (
              <span className="hudt-level" aria-label={`Level ${player.level}`}>
                <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)' }}>
                  Lv {player.level}
                </span>
                <span aria-hidden="true">Lv</span>
                <b aria-hidden="true">{player.level}</b>
              </span>
            ) : (
              <span>Lv {player.level}</span>
            )}
          </div>
          <span
            className={`player-frame-calling${hudStyle ? ' hudt-calling' : ''}`}
            style={{ color: classCol }}
          >
            <span aria-hidden>{getClassIcon(player.class)}</span> {formatClassName(player.class)}
          </span>
        </div>
      </header>

      {player.class === 'rogue' && <ComboPointRow points={combo.points} max={combo.max} />}

      <VitalsPanel
        player={player}
        regenerating={regenerating}
        inCombat={inCombat}
        hudStyle={hudStyle}
      />

      {((player.statPoints ?? 0) > 0 || (player.skillPoints ?? 0) > 0) && (
        <div className="player-frame-alerts" data-testid="player-frame-alerts">
          {(player.statPoints ?? 0) > 0 && (
            <button
              type="button"
              className="player-frame-alert-badge stat-badge"
              data-testid="player-frame-stat-alert"
              onClick={() => windowBridge.open('equipment')}
              title={`${player.statPoints} unspent stat points. Press C or click to allocate.`}
            >
              <span className="player-frame-alert-icon" aria-hidden>✨</span>
              <span>+{player.statPoints} Stat (C)</span>
            </button>
          )}
          {(player.skillPoints ?? 0) > 0 && (
            <button
              type="button"
              className="player-frame-alert-badge talent-badge"
              data-testid="player-frame-talent-alert"
              onClick={() => windowBridge.open('talents')}
              title={`${player.skillPoints} unspent talent points. Press N or click to view tree.`}
            >
              <span className="player-frame-alert-icon" aria-hidden>🌳</span>
              <span>+{player.skillPoints} Talent (N)</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const MemoPlayerInfoPanel = React.memo(
  PlayerInfoPanelInner,
  (previous, next) =>
    previous.renderKey === next.renderKey && previous.hudStyle === next.hudStyle,
);

export const PlayerInfoPanel: React.FC<{
  player: Helix2000Character;
  hudStyle?: HudConcreteStyle;
}> = ({ player, hudStyle }) => {
  const snap = playerInfoSnapshot(player);
  return <MemoPlayerInfoPanel {...snap} hudStyle={hudStyle} />;
};

PlayerInfoPanel.displayName = 'PlayerInfoPanel';
